/**
 * Per-airport search snippet overrides.
 *
 * Side-by-side addition: the airport page keeps its default title/description
 * logic and only uses an entry here when one exists for that airport code.
 * Written for airports with high search impressions but low click-through.
 */

export interface AirportSnippet {
  /** Title used when a live wait time is available. `{wait}` is replaced with the number of minutes. */
  titleLive: string;
  /** Title used when no live wait time is available. */
  titleFallback: string;
  /** Description used when a live wait time is available. `{wait}` is replaced with the number of minutes. */
  descriptionLive: string;
  /** Description used when no live wait time is available. */
  descriptionFallback: string;
  /** Optional quick terminal tips shown on the airport page. */
  tips?: string[];
}

export const airportSnippets: Record<string, AirportSnippet> = {
  EWR: {
    titleLive: "EWR TSA Wait Time Now – {wait} Min | Terminals A, B & C",
    titleFallback: "EWR TSA Wait Time Today | Newark Terminals A, B & C",
    descriptionLive:
      "Newark TSA wait is {wait} minutes right now. See Terminal A, B and C security lines plus TSA PreCheck and CLEAR times before you leave for EWR.",
    descriptionFallback:
      "Check Newark security before you leave: Terminal A, B and C TSA lines, PreCheck and CLEAR waits, and when to leave for your EWR flight.",
  },
  LAX: {
    titleLive: "LAX TSA Wait Times Now: {wait} Min – Live by Terminal",
    titleFallback: "LAX TSA Wait Times Right Now – Live by Terminal & TBIT",
    descriptionLive:
      "LAX TSA wait times right now: {wait} min. See live security lines for Terminals 1–8 and Tom Bradley, PreCheck lanes, and exactly when to leave for LAX.",
    descriptionFallback:
      "LAX TSA wait times right now for Terminals 1–8 and Tom Bradley International. Check your line, PreCheck lanes and when to leave — before you head to LAX.",
    tips: [
      "LAX has nine terminals in a horseshoe: Terminals 1–8 plus Tom Bradley International (TBIT). Check your airline's terminal before you go — lines differ a lot between them.",
      "Several terminals (4 through 8 and TBIT) are connected airside, so if your checkpoint is packed you may be able to clear security at a neighbouring one.",
      "Taxis and rideshare pick up at the LAX-it lot next to Terminal 1, reached by a free shuttle — allow extra time after you land.",
      "Traffic on the central terminal loop can add 20+ minutes at peak times. Build that into your leave-by time, not just the security wait.",
    ],
  },
};

export const getAirportSnippet = (code?: string): AirportSnippet | undefined =>
  code ? airportSnippets[code.toUpperCase()] : undefined;
